import { Investment, InvestmentResults } from '../../types';

export interface CertificateStatus {
  id: number;
  status: 'reserved' | 'waiting' | 'growing' | 'producing';
  reservedYear: number;
  maturationYear?: number;
  yearPaidOff?: number;
  remainingPayment: number;
  paymentYearsRemaining?: number;
  details: string;
  isContingency?: boolean;
}

export interface YearEvolutionData {
  year: number;
  date: string;
  certificatePrice: number;
  certificates: CertificateStatus[];
  totalUtility: number;
  reinvestmentFund: number;
  availableForReinvestment: number;
  newCertificateIds: number[];
  reservedCertificateIds: number[];
  payments: { id: number; amount: number }[];
  // New fields for integration with main calculations
  totalCertificates: number; // All certificates (fully paid + reserved)
  certificatesFromReinvestment: number; // Cumulative certificates from reinvestment
  citrusPatrimony: number; // Total patrimony value
  citrusIncome: number; // Annual income from producing certificates
  // New fields for cash out tracking
  yearlyCashOutAmount: number; // Amount withdrawn this year
  cumulativeCashOutAmount: number; // Total amount withdrawn so far
  yearlyReinvestmentContribution: number; // Amount contributed to reinvestment fund this year
  cumulativeReinvestmentContribution: number; // Cumulative amount contributed to reinvestment fund
  cumulativeTotalUtility: number; // Cumulative total utility generated
  // New fields for payment boost
  paymentBoostActive: boolean; // Whether payment boost is active this year
  paymentBoostAmount: number; // Amount added by payment boost this year
  // New fields for Propuesta Cosecha & Citrus
  citrusExtraUtility?: number; // Extra utility for Citrus (from price increase and first year)
  citrusNetUtility?: number; // Net utility for Citrus after commissions
  citrusReinvestmentContribution?: number; // Amount Citrus reinvests
  citrusCertificatesFromReinvestment?: number; // Certificates Citrus acquires through reinvestment
  citrusTotalCertificates?: number; // Total certificates owned by Citrus
}

export function getDetailedCertificateEvolution( 
  investment: Investment, 
  initialCertificatesOverride?: number, 
  yearsOverride?: number,
  forceReinvestmentDuringMaturation = false,
  monthlyContributionOverride?: number // Add monthlyContribution as a parameter
): YearEvolutionData[] {
  console.log('getDetailedCertificateEvolution called');
  try {
    const {
      certificateBasePrice,
      appreciationRate,
      reinvestProfits,
      lemonPriceIncrease,
      increaseLemonPrice,
      partialCashOut,
      cashOutPercentage,
      yearlyCashOutPercentages,
      isLongTermCalculator,
      firstYearUtilityToUser,
      commissionRate = 0.05,
      citrusReinvestment = true,
      citrusReinvestmentPercentages = Array(30).fill(100),
      currencyFormat,
      exchangeRate,
      exchangeRateEUR,
      inflationRate, // Keep inflationRate for internal calculations if needed
      monthlyContribution, // Get monthlyContribution from investment
      applyTaxes,
      taxRate,
      additionalContributions,
      averageProductionPerHectare = 24500,
      averageSalePricePerKg = 30
    } = investment;

    // Use override if provided, otherwise use investment.monthlyContribution
    const effectiveMonthlyContribution = monthlyContributionOverride !== undefined ? monthlyContributionOverride : monthlyContribution;

    // Use overrides if provided
    const initialCertificates = initialCertificatesOverride !== undefined ? 
      initialCertificatesOverride : investment.initialCertificates;
    
    const years = yearsOverride !== undefined ? 
      yearsOverride : investment.years;

    // Set minimum reservation percentage for flexible certificate acquisition
    const minReservationPercentage = 0.05; // 5% minimum of certificate price to reserve (facilita apartado temprano)
    const earlyAcquisitionBoost = 0.15; // 15% boost for early acquisition (years 5-7)
   const currencyTolerance = 0.01; // Consider less than 1 cent as fully paid

    // Fix for Traditional Calculator: Force 4-year waiting period when it's NOT a long-term calculator
    // and reinvestment is disabled (Traditional Calculator characteristics)
    const waitingPeriod = isLongTermCalculator ? 5 : 4;
    
    // Additional safety check for Traditional Calculator
    const isTraditionalCalculator = !reinvestProfits && !firstYearUtilityToUser;
    const effectiveWaitingPeriod = isTraditionalCalculator ? 4 : waitingPeriod;
    
    const paymentPeriod = 4;

    const effectiveCertificateBasePrice = isLongTermCalculator ? 
      certificateBasePrice * 1.25 : certificateBasePrice;

    const evolutionData: YearEvolutionData[] = [];
    
    // Initialize certificates array
    const certificates: {
      id: number;
      reservedYear: number;
      maturationYear?: number;
      yearPaidOff?: number;
      initialPrice: number;
      remainingPayment: number;
      paymentYearsRemaining: number;
      status: 'reserved' | 'waiting' | 'growing' | 'producing';
      annualPaymentDue?: number;
    }[] = [];

    // Initialize certificates
    const totalInitialCost = initialCertificates * effectiveCertificateBasePrice;
    const initialPaymentPerCertificate = investment.initialPayment / initialCertificates;
    const remainingPaymentPerCertificate = Math.max(0, effectiveCertificateBasePrice - initialPaymentPerCertificate);
    const isPaid = remainingPaymentPerCertificate < currencyTolerance;

    for (let i = 0; i < initialCertificates; i++) {
      const annualPaymentDue = isPaid ? 0 : remainingPaymentPerCertificate / paymentPeriod;
      const maturationYear = 1 + effectiveWaitingPeriod;
      const yearPaidOff = isPaid ? 1 : undefined;
      
      certificates.push({
        id: i + 1,
        reservedYear: 1,
        maturationYear: maturationYear,
        yearPaidOff: yearPaidOff,
        initialPrice: effectiveCertificateBasePrice,
        remainingPayment: isPaid ? 0 : remainingPaymentPerCertificate,
        paymentYearsRemaining: isPaid ? 0 : Math.ceil(remainingPaymentPerCertificate / (remainingPaymentPerCertificate / paymentPeriod)),
        status: isPaid ? 'waiting' : 'reserved',
        annualPaymentDue
      });
    }

    let nextCertificateId = initialCertificates + 1;
    let liquidCashForReinvestment = Math.max(0, investment.initialPayment - totalInitialCost);
    let totalAccumulatedReinvestmentFunds = 0;
    let certificatesFromReinvestment = 0;
    let totalCashOut = 0;
    let cumulativeReinvestmentContribution = 0;
    let cumulativeTotalUtility = 0;
    let firstYearUtility = 0;
    
    // Citrus tracking
    let citrusExtraUtilityTotal = 0;
    let citrusNetUtilityTotal = 0;
    let citrusCertificatesFromReinvestment = 0;
    let citrusReinvestmentFund = 0;

    // Main calculation loop
    for (let year = 1; year <= years; year++) {
      // Initialize shouldReinvest at the beginning of each year loop
      let shouldReinvest = false;
      
      // Calculate certificate price - ONLY for FULLY PAID certificates
      let certificatePrice: number;
      if (year <= 5) {
        certificatePrice = effectiveCertificateBasePrice * Math.pow(1 + appreciationRate / 100, year);
      } else {
        const maxAppreciatedValue = effectiveCertificateBasePrice * Math.pow(1 + appreciationRate / 100, 5);
        if (increaseLemonPrice) {
          certificatePrice = maxAppreciatedValue * Math.pow(1 + lemonPriceIncrease / 100, year - 5);
        } else {
          certificatePrice = maxAppreciatedValue;
        }
      }

      let newCertificateIds: number[] = [];
      let reservedCertificateIds: number[] = [];
      let payments: { id: number; amount: number }[] = [];
      let yearlyCashOutAmount = 0;
      let yearlyReinvestmentContribution = 0;
      let citrusReinvestmentContribution = 0;
      let isFirstProductionYear = false;
      let paymentBoostActive = false;
      let paymentBoostAmount = 0;

      // Add user's monthly contributions to the liquid cash available for payments
      if (investment.additionalContributions && year >= 5) {
        liquidCashForReinvestment += effectiveMonthlyContribution * 12; // Annualize monthly contribution
      }

      // Calculate utility from all producing certificates FIRST
      let yearUtility = 0;
      certificates.forEach(cert => {
        const productionStartYear = cert.maturationYear || (cert.reservedYear + effectiveWaitingPeriod);
        // CRITICAL: Certificate can ONLY produce if it's 100% paid AND has completed maturation period
        const isFullyPaid = cert.remainingPayment < currencyTolerance;
        const hasMatured = year >= productionStartYear;
        const isNotReserved = cert.status !== 'reserved';
        
        // Triple verification: must be fully paid, matured, and not in reserved status
        if (isFullyPaid && hasMatured && isNotReserved) {
          // Calculate utility using new formula with lemon price increment
          let currentLemonPrice = averageSalePricePerKg;
          if (year > 5 && increaseLemonPrice) {
            // Apply lemon price increase for years after 5
            currentLemonPrice = averageSalePricePerKg * Math.pow(1 + lemonPriceIncrease / 100, year - 5);
          }

          // Each certificate = 0.1 hectare, investor gets 65% of profits
          const annualUtilityPerCertificate = averageProductionPerHectare * currentLemonPrice * 0.1 * 0.65;
          yearUtility += annualUtilityPerCertificate;
        }
      });

      // Determine how much of this year's utility goes to reinvestment fund
      let currentYearReinvestmentContribution = 0;
      if (yearUtility > 0) {
        shouldReinvest = reinvestProfits || (forceReinvestmentDuringMaturation && year <= effectiveWaitingPeriod);

        if (shouldReinvest) {
          if (partialCashOut) {
            const yearIndex = year - 1;
            const yearPercentage = yearlyCashOutPercentages && yearlyCashOutPercentages[yearIndex] !== undefined
              ? yearlyCashOutPercentages[yearIndex]
              : cashOutPercentage;

            const cashOutAmount = yearUtility * (yearPercentage / 100);
            yearlyCashOutAmount = cashOutAmount;
            totalCashOut += cashOutAmount;
            currentYearReinvestmentContribution = yearUtility - cashOutAmount;
          } else {
            currentYearReinvestmentContribution = yearUtility;
          }
        }
      }

      // Track reinvestment contribution
      yearlyReinvestmentContribution = currentYearReinvestmentContribution;
      cumulativeReinvestmentContribution += currentYearReinvestmentContribution;
      cumulativeTotalUtility += yearUtility;

      // Add current year's reinvestment contribution to available funds for payments
      liquidCashForReinvestment += currentYearReinvestmentContribution;

      // Add early acquisition boost for years 5-7 to accelerate certificate acquisition
      if (year >= 5 && year <= 7 && shouldReinvest) {
        const boostAmount = currentYearReinvestmentContribution * earlyAcquisitionBoost;
        liquidCashForReinvestment += boostAmount;
      }

      // Define variables needed for payment boost logic
      const initialCertificatesFullyPaid = certificates
        .filter(cert => cert.id <= initialCertificates)
        .every(cert => cert.remainingPayment <= 0);
      
      const withinBoostYears = !investment.paymentBoostYears || 
        (year - paymentPeriod - 1) <= investment.paymentBoostYears;

      // Check if payment boost should be active
      if (investment.enablePaymentBoost && reinvestProfits) {
        if (initialCertificatesFullyPaid && year > paymentPeriod && withinBoostYears) {
          paymentBoostActive = true;
          // Use custom boost amount or calculate the original monthly payment amount
          if (investment.paymentBoostAmount) {
            // Apply linear growth based on paymentBoostGrowthRate
            const baseAmount = investment.paymentBoostAmount;
            const growthRate = investment.paymentBoostGrowthRate || 0;
            const yearsOfGrowth = year - paymentPeriod - 1; // Years since boost started
            paymentBoostAmount = baseAmount * Math.pow(1 + growthRate / 100, yearsOfGrowth);
          } else {
            const originalMonthlyPayment = (initialCertificates * effectiveCertificateBasePrice * 0.7) / 48;
            const baseAmount = originalMonthlyPayment * 12; // Annual boost amount
            const growthRate = investment.paymentBoostGrowthRate || 0;
            const yearsOfGrowth = year - paymentPeriod - 1; // Years since boost started
            paymentBoostAmount = baseAmount * Math.pow(1 + growthRate / 100, yearsOfGrowth);
          }
          // Only add boost amount if we're actually reinvesting
          if (shouldReinvest) {
            liquidCashForReinvestment += paymentBoostAmount;
          }
        }
      }

      // DEBUG: Track initial liquid cash for this year
      const initialLiquidCash = liquidCashForReinvestment;
      let totalPaymentsMade = 0;
      let totalNewCertificatesCost = 0;
      
      // Reset tracking variables at the beginning of each year
      totalPaymentsMade = 0;
      totalNewCertificatesCost = 0;
      
      // Sort certificates to prioritize payment of oldest certificates first
      certificates.sort((a, b) => {
        // First priority: certificates with remaining payment (those that need to be paid)
        if (a.remainingPayment > 0 && b.remainingPayment <= 0) return -1;
        if (a.remainingPayment <= 0 && b.remainingPayment > 0) return 1;
        
        // Second priority: oldest certificates first (by reservation year)
        if (a.reservedYear !== b.reservedYear) {
          return a.reservedYear - b.reservedYear;
        }
        
        // Third priority: by certificate ID for consistent ordering
        return a.id - b.id;
      });

      // Process certificate payments and status updates
      for (let i = 0; i < certificates.length; i++) {
        const cert = certificates[i];

        // FIRST: Process scheduled payments for initial certificates (reserved in year 1)
        // FIRST: Process scheduled payments for ALL certificates with remaining payment
        if (cert.remainingPayment > 0 && cert.paymentYearsRemaining > 0 && cert.annualPaymentDue > 0) {
          // Calculate the scheduled annual payment for this certificate
          const scheduledPayment = cert.annualPaymentDue || 0;
          
          if (scheduledPayment > 0) {
            // Apply the scheduled payment
            const paymentToApply = Math.min(scheduledPayment, cert.remainingPayment);
            cert.remainingPayment -= paymentToApply;
            // Only reduce payment years if we made a full scheduled payment
            if (paymentToApply >= scheduledPayment) {
              cert.paymentYearsRemaining = Math.max(0, cert.paymentYearsRemaining - 1);
            }
            
            // Add to payments array
            payments.push({ id: cert.id, amount: paymentToApply });
            totalPaymentsMade += paymentToApply;
            
            // Check if certificate is now fully paid
            if (cert.remainingPayment < currencyTolerance) {
              cert.remainingPayment = 0;
              cert.paymentYearsRemaining = 0;
              cert.yearPaidOff = year;
              newCertificateIds.push(cert.id);
              
              if (cert.status === 'reserved') {
                cert.status = 'waiting';
              }
            }
          }
        }

        // SECOND: Use available liquid cash for accelerated payments (only if there's still a balance)
        if (cert.remainingPayment > 0 && liquidCashForReinvestment > 0) {
          // Use available funds to accelerate payments (only for remaining balance after scheduled payments)
          const payment = Math.min(liquidCashForReinvestment, cert.remainingPayment);
          
          if (payment > 0) {
            cert.remainingPayment -= payment;
            liquidCashForReinvestment -= payment;
            totalPaymentsMade += payment;
            
            // Add to payments array (or update existing payment for this certificate)
            const existingPaymentIndex = payments.findIndex(p => p.id === cert.id);
            if (existingPaymentIndex >= 0) {
              payments[existingPaymentIndex].amount += payment;
            } else {
              payments.push({ id: cert.id, amount: payment });
            }
            
            if (cert.remainingPayment < currencyTolerance) {
              cert.remainingPayment = 0;
              cert.paymentYearsRemaining = 0;
              cert.yearPaidOff = year;
              
              // Only add to newCertificateIds if not already added
              if (!newCertificateIds.includes(cert.id)) {
                newCertificateIds.push(cert.id);
              }
              
              if (cert.status === 'reserved') {
                cert.status = 'waiting';
              }
            }
          }
        }
      }

      // STEP 1: AGGRESSIVE CERTIFICATE ACQUISITION
      // Prioritize acquiring new certificates with available funds
      shouldReinvest = reinvestProfits || (forceReinvestmentDuringMaturation && year <= effectiveWaitingPeriod);
      
      // Calculate target certificates to acquire this year based on initial certificates
      let targetCertificatesToAcquireThisYear = 0;
      
      if (year < 5) {
        targetCertificatesToAcquireThisYear = 0; // No acquisition before year 5
      } else if (year >= 5 && year <= 10) {
        targetCertificatesToAcquireThisYear = initialCertificates; // Same as initial certificates
      } else if (year > 10) {
        // Calculate multiplier based on 5-year blocks after year 10
        const blocksAfterYear10 = Math.floor((year - 11) / 5);
        const multiplier = Math.pow(2, blocksAfterYear10 + 1); // Starts at 2x, then 4x, 8x, etc.
        targetCertificatesToAcquireThisYear = initialCertificates * multiplier;
      }
      
      // Use dynamic reservation percentage - lower in early years to accelerate acquisition
      const dynamicReservationPercentage = year <= 7 ? minReservationPercentage * 0.8 : minReservationPercentage;
      
      if (shouldReinvest && year >= 5 && targetCertificatesToAcquireThisYear > 0) {
        // Attempt to acquire the target number of certificates this year
        for (let i = 0; i < targetCertificatesToAcquireThisYear; i++) {
          // Check if we have enough funds and haven't reached the certificate limit
          if (liquidCashForReinvestment < certificatePrice * dynamicReservationPercentage || 
              certificates.length >= 1000) {
            break; // Exit if not enough funds or reached limit
          }
          
          // Calculate payment amount - try to pay full price if possible
          const paymentMade = Math.min(liquidCashForReinvestment, certificatePrice);
          
          liquidCashForReinvestment -= paymentMade;
          totalNewCertificatesCost += paymentMade;
          certificatesFromReinvestment++;
          
          const remainingPayment = certificatePrice - paymentMade;
          
          // Safety check: remainingPayment should never be negative
          if (remainingPayment < 0) {
            console.error(`ERROR: Negative remaining payment detected for certificate ${nextCertificateId}: ${remainingPayment}`);
          }
          
          const maturationYear = year + effectiveWaitingPeriod;
          // Calculate flexible payment period based on remaining amount and available funds
          const flexiblePaymentPeriod = Math.max(1, Math.ceil(remainingPayment / (liquidCashForReinvestment * 0.1 + 1000))); // Minimum 1 year, based on available funds
          const annualPaymentDue = remainingPayment > 0 ? remainingPayment / flexiblePaymentPeriod : 0;
          
          certificates.push({
            id: nextCertificateId,
            reservedYear: year,
            maturationYear: maturationYear,
            yearPaidOff: remainingPayment <= currencyTolerance ? year : undefined, // If fully paid, mark as paid off this year
            initialPrice: certificatePrice,
            remainingPayment: remainingPayment,
            paymentYearsRemaining: remainingPayment > 0 ? flexiblePaymentPeriod : 0,
            status: remainingPayment > 0 ? 'reserved' : 'waiting', // 'reserved' if balance remains, 'waiting' if fully paid
            annualPaymentDue: annualPaymentDue
          });
          
          if (remainingPayment > currencyTolerance) reservedCertificateIds.push(nextCertificateId);
          payments.push({ id: nextCertificateId, amount: paymentMade });
          
          nextCertificateId++;
        }
      }

      // DEBUG: Verify fund balance consistency
      const finalLiquidCash = liquidCashForReinvestment;
      const totalSpentThisYear = totalPaymentsMade + totalNewCertificatesCost;
      const expectedFinalCash = initialLiquidCash - totalSpentThisYear;
      
      // Log discrepancy if any (for debugging)
      if (Math.abs(finalLiquidCash - expectedFinalCash) > currencyTolerance && totalSpentThisYear > 0) {
        // Only log if there was actual spending this year
        console.debug(`Year ${year}: Fund balance discrepancy: ${Math.abs(finalLiquidCash - expectedFinalCash).toFixed(2)}`);
      }
      // STEP 2: PROCESS SCHEDULED AND ACCELERATED PAYMENTS
      // Update certificate statuses
      certificates.forEach(cert => {
        // CRITICAL: Certificate can only change status when 100% paid
        if (cert.remainingPayment >= currencyTolerance) {
          cert.status = 'reserved';
        } else {
          // Only change status when certificate is 100% paid
          const productionStartYear = cert.maturationYear || (cert.reservedYear + effectiveWaitingPeriod);
          
          // Safety check: ensure remainingPayment is not negative
          if (cert.remainingPayment < 0) {
            console.error(`ERROR: Certificate ${cert.id} has negative remaining payment: ${cert.remainingPayment}`);
            cert.remainingPayment = 0; // Force to zero to prevent display issues
          }
          
          if (year < productionStartYear) {
            cert.status = 'waiting';
          } else if (year === productionStartYear) {
            cert.status = 'growing';
          } else {
            cert.status = 'producing';
          }
        }
      });

      // Generate certificate status data
      const certificatesData: CertificateStatus[] = certificates.map(cert => {
        const yearsFromReservation = year - cert.reservedYear;
        // CRÍTICO: El certificado SOLO puede producir si está 100% pagado Y ha completado el período de maduración
        
        // Additional safety check for display
        if (cert.remainingPayment < 0) {
          console.warn(`WARNING: Certificate ${cert.id} has negative remaining payment for display: ${cert.remainingPayment}. Setting to 0.`);
          cert.remainingPayment = 0;
        }
        
        let details = '';
        
        if (cert.status === 'reserved') {
          const percentagePaid = ((cert.initialPrice - cert.remainingPayment) / cert.initialPrice) * 100;
          details = `Certificado ${cert.id} en proceso de pago (${Math.round(percentagePaid)}% pagado) - Apartado año ${cert.reservedYear}`;
        } else if (cert.status === 'waiting') {
          const productionStartYear = cert.maturationYear || (cert.reservedYear + effectiveWaitingPeriod);
          const yearsToProduction = productionStartYear - year;
          details = `Certificado ${cert.id} en período de maduración: ${yearsToProduction} años restantes - Apartado año ${cert.reservedYear}`;
        } else if (cert.status === 'growing') {
          details = `Certificado ${cert.id} iniciando producción (primer año) - Apartado año ${cert.reservedYear}`;
        } else {
          const yearsProducing = cert.maturationYear ? (year - cert.maturationYear) + 1 : 1;
          details = `Certificado ${cert.id} en producción: ${yearsProducing} años - Apartado año ${cert.reservedYear}`;
        }
        
        return {
          id: cert.id,
          status: cert.status,
          reservedYear: cert.reservedYear,
          maturationYear: cert.maturationYear,
          yearPaidOff: cert.yearPaidOff,
          paymentYearsRemaining: cert.paymentYearsRemaining,
          // Add safety check for negative values in the returned data
          remainingPayment: Math.max(0, cert.remainingPayment),
          initialPrice: cert.initialPrice || certificatePrice
        };
        
        return {
          id: cert.id,
          status: cert.status,
          reservedYear: cert.reservedYear,
          maturationYear: cert.maturationYear,
          yearPaidOff: cert.yearPaidOff,
          remainingPayment: Math.max(0, cert.remainingPayment), // Ensure never negative
          paymentYearsRemaining: cert.paymentYearsRemaining,
          details
        };
      });

      // Calculate totals - CRITICAL: Only count FULLY PAID certificates for patrimony
      const totalCertificates = certificates.length;
      
      // FIXED: Calculate patrimony only for FULLY PAID certificates
      let citrusPatrimony = 0;
      certificates.forEach(cert => {
        const isFullyPaid = cert.remainingPayment < currencyTolerance;
        if (isFullyPaid) {
          // Use the current certificate price for fully paid certificates
          citrusPatrimony += certificatePrice;
        } else {
          // For certificates still being paid, only count what has been paid
          const paidAmount = cert.initialPrice - cert.remainingPayment;
          citrusPatrimony += paidAmount;
        }
      });
      
      const citrusIncome = yearUtility;

      // Format date
      const currentDate = new Date(2024, 0, 1);
      currentDate.setFullYear(currentDate.getFullYear() + year - 1);
      const formattedDate = currentDate.toLocaleDateString('es-MX', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });

      evolutionData.push({
        year,
        date: formattedDate,
        certificatePrice,
        certificates: certificatesData,
        totalUtility: yearUtility,
        reinvestmentFund: totalAccumulatedReinvestmentFunds,
        availableForReinvestment: liquidCashForReinvestment,
        newCertificateIds,
        reservedCertificateIds,
        payments,
        totalCertificates,
        certificatesFromReinvestment,
        citrusPatrimony,
        citrusIncome,
        yearlyCashOutAmount,
        cumulativeCashOutAmount: totalCashOut,
        yearlyReinvestmentContribution,
        cumulativeReinvestmentContribution,
        cumulativeTotalUtility,
        paymentBoostActive,
        paymentBoostAmount,
        citrusReinvestmentContribution,
        citrusTotalCertificates: citrusCertificatesFromReinvestment
      });
    }

    console.log(`Evolution calculation completed. Final certificate count: ${certificates.length}`);
    return evolutionData;
  } catch (error) {
    console.error('Error in getDetailedCertificateEvolution:', error);
    return [];
  }
}

// Helper function to format currency (simplified version)
function formatCurrency(value: number): string {
  try {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      maximumFractionDigits: 0,
    }).format(value);
  } catch (error) {
    console.error('Error in formatCurrency:', error);
    return `$${value}`;
  }
}

export function getCertificatePhasesSummary(evolutionData: YearEvolutionData[], targetYear: number): {
  reserved: number;
  waiting: number;
  growing: number;
  producing: number;
} {
  try {
    const targetYearData = evolutionData.find(data => data.year === targetYear);
    
    if (!targetYearData) {
      return { reserved: 0, waiting: 0, growing: 0, producing: 0 };
    }

    const summary = {
      reserved: 0,
      waiting: 0,
      growing: 0,
      producing: 0
    };

    targetYearData.certificates.forEach(cert => {
      summary[cert.status]++;
    });

    return summary;
  } catch (error) {
    console.error('Error in getCertificatePhasesSummary:', error);
    return { reserved: 0, waiting: 0, growing: 0, producing: 0 };
  }
}